import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    Rss,
    Search,
    Languages,
    X,
    ExternalLink,
    RefreshCcw,
    Eye,
    Check,
    Loader2,
    CheckSquare,
    Square,
} from "lucide-react";

const STATUS_TABS = [
    { id: "pending", label: "Pending Review" },
    { id: "imported", label: "Imported" },
    { id: "dismissed", label: "Dismissed" },
    { id: "all", label: "All" },
];

const NewsFeedCuration = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [status, setStatus] = useState("pending");
    const [source, setSource] = useState("all");
    const [search, setSearch] = useState("");
    const [importingId, setImportingId] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkImporting, setBulkImporting] = useState(false);

    const authHeaders = () => {
        const token = localStorage.getItem("dimbani_token");
        return { Authorization: `Bearer ${token}` };
    };

    const fetchItems = async (forceRefresh = false) => {
        if (forceRefresh) setRefreshing(true);
        else setLoading(true);
        try {
            const params = new URLSearchParams({ limit: 50, status });
            if (source !== "all") params.set("source", source);
            if (search) params.set("search", search);
            if (forceRefresh) params.set("refresh", "true");
            const { data } = await axios.get(
                `${API}/admin/external-news?${params.toString()}`,
                { headers: authHeaders() }
            );
            setItems(data?.items || []);
            setTotal(data?.total || 0);
            setFeeds(data?.feeds || []);
        } catch (error) {
            toast.error("Failed to load news feed");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchItems();
        setSelected(new Set());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, source]);

    const toggleSelected = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const selectablePendingIds = items
        .filter((it) => !it.imported_content_id && !it.dismissed)
        .map((it) => it.id);

    const allSelected =
        selectablePendingIds.length > 0 &&
        selectablePendingIds.every((id) => selected.has(id));

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(selectablePendingIds));
        }
    };

    const handleBulkImport = async () => {
        const ids = [...selected].slice(0, 20);
        if (ids.length === 0) return;
        if (
            !window.confirm(
                `Import and translate ${ids.length} article${ids.length > 1 ? "s" : ""} to Swahili? This may take up to ${ids.length * 4} seconds.`
            )
        )
            return;
        setBulkImporting(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/external-news/bulk-import`,
                { item_ids: ids },
                { headers: authHeaders() }
            );
            toast.success(
                `Imported ${data.imported}, skipped ${data.skipped}, errors ${data.errors}. Drafts saved in Content.`
            );
            setSelected(new Set());
            fetchItems();
        } catch (error) {
            toast.error(
                error.response?.data?.detail || "Bulk import failed. Try again."
            );
        } finally {
            setBulkImporting(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchItems();
    };

    const handleImport = async (item) => {
        setImportingId(item.id);
        try {
            const { data } = await axios.post(
                `${API}/admin/external-news/${encodeURIComponent(item.id)}/import`,
                {},
                { headers: authHeaders() }
            );
            toast.success(
                "Imported & translated to Swahili. Review and publish in Content."
            );
            // Navigate to Content Management so user can edit + publish
            navigate(`/admin/content?edit=${data.id}`);
        } catch (error) {
            toast.error(
                error.response?.data?.detail || "Import failed. Try again."
            );
        } finally {
            setImportingId(null);
        }
    };

    const handleDismiss = async (item) => {
        try {
            await axios.post(
                `${API}/admin/external-news/${encodeURIComponent(item.id)}/dismiss`,
                {},
                { headers: authHeaders() }
            );
            toast.success("Item dismissed");
            fetchItems();
        } catch {
            toast.error("Failed to dismiss item");
        }
    };

    const sources = [...new Set(feeds.map((f) => f.source))];

    const formatTime = (s) => {
        if (!s) return "";
        const d = new Date(s);
        if (isNaN(d.getTime())) return "";
        const diff = Math.floor((Date.now() - d.getTime()) / 60000);
        if (diff < 1) return "just now";
        if (diff < 60) return `${diff}m ago`;
        if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
        return d.toLocaleDateString();
    };

    return (
        <AdminLayout title="News Feed Curation">
            <div className="space-y-6" data-testid="news-feed-curation">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Rss className="h-6 w-6 text-electric" />
                        <div>
                            <h1 className="text-2xl font-heading font-bold text-white">
                                News Feed Curation
                            </h1>
                            <p className="text-white/50 text-sm">
                                Review articles aggregated from BBC, Sky Sports, ESPN, CNN, Guardian, FIFA, CAF. Pick ones to translate into Swahili and publish on the public site.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-white/40 text-xs">{total} items</span>
                        <Button
                            variant="outline"
                            onClick={() => fetchItems(true)}
                            disabled={refreshing}
                            className="border-electric/30 text-electric hover:bg-electric/10"
                            data-testid="refresh-feed-btn"
                        >
                            <RefreshCcw
                                className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`}
                            />
                            Refresh Feeds
                        </Button>
                    </div>
                </div>

                {/* Status Tabs */}
                <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide border-b border-border">
                    {STATUS_TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setStatus(tab.id)}
                            data-testid={`status-tab-${tab.id}`}
                            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                status === tab.id
                                    ? "border-electric text-electric"
                                    : "border-transparent text-white/60 hover:text-white"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                    <form
                        onSubmit={handleSearch}
                        className="relative flex-1 min-w-[220px] max-w-md"
                    >
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                        <Input
                            placeholder="Search headlines..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 bg-subtle border-border text-white h-9"
                            data-testid="feed-search"
                        />
                    </form>
                    <Select value={source} onValueChange={setSource}>
                        <SelectTrigger className="w-44 bg-subtle border-border text-white h-9">
                            <SelectValue placeholder="Source" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Sources</SelectItem>
                            {sources.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Bulk action toolbar (only on Pending tab) */}
                {status === "pending" && selectablePendingIds.length > 0 && (
                    <div
                        className="flex items-center justify-between gap-3 p-3 bg-subtle border border-border rounded"
                        data-testid="bulk-toolbar"
                    >
                        <button
                            onClick={toggleSelectAll}
                            className="flex items-center gap-2 text-white/80 hover:text-electric text-sm"
                            data-testid="select-all-btn"
                        >
                            {allSelected ? (
                                <CheckSquare className="h-4 w-4 text-electric" />
                            ) : (
                                <Square className="h-4 w-4" />
                            )}
                            <span className="font-medium">
                                {allSelected ? "Deselect all" : "Select all on page"}
                            </span>
                            {selected.size > 0 && (
                                <Badge className="bg-electric/20 text-electric border-electric/30 ml-2">
                                    {selected.size} selected
                                </Badge>
                            )}
                        </button>
                        <Button
                            onClick={handleBulkImport}
                            disabled={selected.size === 0 || bulkImporting}
                            className="bg-electric hover:bg-vodaredDark text-white"
                            data-testid="bulk-import-btn"
                        >
                            {bulkImporting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Translating {selected.size}...
                                </>
                            ) : (
                                <>
                                    <Languages className="h-4 w-4 mr-2" />
                                    Import &amp; Translate ({selected.size > 0 ? selected.size : 0})
                                </>
                            )}
                        </Button>
                    </div>
                )}

                {/* Items Grid */}
                {loading ? (
                    <div className="text-center py-16 text-white/40">Loading feed...</div>
                ) : items.length === 0 ? (
                    <Card className="bg-paper border-border p-12 text-center">
                        <Rss className="h-12 w-12 text-white/20 mx-auto mb-3" />
                        <p className="text-white/60">No items in this category.</p>
                        <p className="text-white/40 text-xs mt-1">
                            Try a different tab, or click Refresh Feeds to pull the latest.
                        </p>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {items.map((item, idx) => (
                            <FeedCard
                                key={item.id}
                                item={item}
                                idx={idx}
                                formatTime={formatTime}
                                onImport={handleImport}
                                onDismiss={handleDismiss}
                                importing={importingId === item.id}
                                isSelected={selected.has(item.id)}
                                onToggleSelect={() => toggleSelected(item.id)}
                                selectable={status === "pending" && !item.imported_content_id && !item.dismissed}
                            />
                        ))}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

const FeedCard = ({ item, idx, formatTime, onImport, onDismiss, importing, isSelected, onToggleSelect, selectable }) => {
    const imported = !!item.imported_content_id;
    const dismissed = item.dismissed;
    const fallback =
        "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=600";

    return (
        <Card
            className={`bg-paper border overflow-hidden hover:border-electric/40 transition-colors flex flex-col ${
                isSelected ? "border-electric" : "border-border"
            }`}
            data-testid={`feed-item-${idx}`}
        >
            <div className="aspect-[16/9] bg-subtle overflow-hidden relative">
                <img
                    src={item.image || fallback}
                    alt={item.title}
                    onError={(e) => (e.currentTarget.src = fallback)}
                    className="w-full h-full object-cover"
                />
                <div className="absolute top-2 left-2 flex gap-1 items-center">
                    {selectable && (
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onToggleSelect();
                            }}
                            className={`h-6 w-6 rounded flex items-center justify-center border ${
                                isSelected
                                    ? "bg-electric border-electric text-white"
                                    : "bg-black/60 border-white/40 text-white/70 hover:text-white"
                            }`}
                            title={isSelected ? "Deselect" : "Select for bulk import"}
                            data-testid={`select-${idx}`}
                        >
                            {isSelected ? (
                                <Check className="h-3.5 w-3.5" />
                            ) : (
                                <Square className="h-3.5 w-3.5" />
                            )}
                        </button>
                    )}
                    <Badge
                        variant="outline"
                        className="bg-black/60 border-electric/40 text-electric text-[10px] uppercase"
                    >
                        {item.source}
                    </Badge>
                    {imported && (
                        <Badge className="bg-electric/80 text-white text-[10px] uppercase">
                            <Check className="h-2.5 w-2.5 mr-0.5" /> Imported
                        </Badge>
                    )}
                    {dismissed && (
                        <Badge className="bg-white/20 text-white text-[10px] uppercase">
                            Dismissed
                        </Badge>
                    )}
                </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-white/50 text-xs">{item.feed_name}</span>
                    <span className="text-white/40 text-xs">
                        {formatTime(item.published)}
                    </span>
                </div>
                <h3
                    className="text-white font-heading font-bold text-base leading-snug mb-2 line-clamp-3"
                    data-testid={`feed-title-${idx}`}
                >
                    {item.title}
                </h3>
                {item.summary && (
                    <p className="text-white/60 text-xs line-clamp-3 mb-3">
                        {item.summary}
                    </p>
                )}

                <div className="mt-auto pt-3 border-t border-border/60 flex items-center gap-2">
                    {imported ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                (window.location.href = `/admin/content?edit=${item.imported_content_id}`)
                            }
                            className="flex-1 border-electric/30 text-electric hover:bg-electric/10"
                            data-testid={`open-content-${idx}`}
                        >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            Open Draft
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            onClick={() => onImport(item)}
                            disabled={importing}
                            className="flex-1 bg-electric hover:bg-vodaredDark text-white"
                            data-testid={`import-${idx}`}
                        >
                            {importing ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                    Translating...
                                </>
                            ) : (
                                <>
                                    <Languages className="h-3.5 w-3.5 mr-1" />
                                    Import & Translate
                                </>
                            )}
                        </Button>
                    )}
                    {item.link && (
                        <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/50 hover:text-electric p-2 rounded"
                            title="Open original article"
                        >
                            <ExternalLink className="h-4 w-4" />
                        </a>
                    )}
                    {!imported && !dismissed && (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onDismiss(item)}
                            className="text-white/50 hover:text-red-400"
                            title="Dismiss"
                            data-testid={`dismiss-${idx}`}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default NewsFeedCuration;
